import { RetryLink } from '@apollo/client/link/retry';
import { Operation } from '@apollo/client';
import { ILogger } from '@bloomays-lib/types.shared';
import { isBrowserOffline } from '@bloomays-lib/utils.shared';
import { Auth0TokenExpiredError } from '../../services/authService';

/**
 * HTTP status codes that indicate a transient upstream/gateway failure worth
 * retrying: Bad Gateway, Service Unavailable, Gateway Timeout. Any other
 * status (including generic 500 Internal Server Error or transport-level
 * fetch failures without a status) is treated as non-retryable.
 */
const RETRYABLE_STATUS_CODES = new Set<number>([502, 503, 504]);

/**
 * Extracts whichever HTTP status is reachable on an Apollo network error
 * (Response object, ServerError, or fetch TypeError).
 */
const extractStatusCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const err = error as { statusCode?: number; status?: number; response?: { status?: number } };
  return err.statusCode ?? err.status ?? err.response?.status;
};

const containsFileUpload = (variables: Record<string, unknown> | undefined): boolean => {
  if (!variables) return false;
  return Object.values(variables).some((value) => {
    if (!value || typeof value !== 'object') return false;
    if (typeof File !== 'undefined' && value instanceof File) return true;
    if (typeof Blob !== 'undefined' && value instanceof Blob) return true;
    return false;
  });
};

export interface CreateRetryLinkOptions {
  logger?: ILogger;
  /** Total attempts including the initial one. Defaults to 3 (initial + 2 retries). */
  maxAttempts?: number;
  /** Initial delay in ms before the first retry. Defaults to 300ms. */
  initialDelayMs?: number;
  /** Cap for the exponential delay in ms. Defaults to 3000ms. */
  maxDelayMs?: number;
}

/**
 * Pure predicate deciding whether an Apollo network error is worth retrying.
 * Exported for unit tests so the retry contract can be locked without booting
 * the whole ApolloClient / Observable machinery.
 */
export const shouldRetryApolloError = (error: unknown, operation: Operation, logger?: ILogger): boolean => {
  if (isBrowserOffline()) {
    logger?.warn('[apollo.retry] browser is offline, skipping retry', {
      operationName: operation.operationName,
    });
    return false;
  }

  if (error instanceof Auth0TokenExpiredError) return false;

  if (containsFileUpload(operation.variables as Record<string, unknown> | undefined)) {
    return false;
  }

  const status = extractStatusCode(error);
  if (typeof status === 'number' && RETRYABLE_STATUS_CODES.has(status)) {
    logger?.warn('[apollo.retry] retrying after upstream gateway error', {
      operationName: operation.operationName,
      status,
    });
    return true;
  }

  return false;
};

/**
 * Apollo link that automatically retries a narrow set of transient upstream
 * failures: HTTP 502 (Bad Gateway), 503 (Service Unavailable) and 504
 * (Gateway Timeout). We deliberately avoid retrying:
 * - GraphQL business errors (they never trigger `retryIf` — RetryLink only sees networkError)
 * - Auth0 token refresh failures (handled by the provider, not the transport)
 * - Transport-level fetch failures without an HTTP status (`TypeError: Failed to fetch`,
 *   which almost always signals an offline browser or an aborted request)
 * - Any HTTP status outside 502/503/504 (including 4xx client errors and a
 *   generic 500 Internal Server Error, which usually indicates a bug rather
 *   than a transient hiccup)
 * - File upload mutations (avoid re-uploading large multipart payloads)
 * - Requests made while the browser reports itself offline (fail fast and let
 *   the offline detection UI/monitoring handle it)
 */
export const createRetryLink = (options: CreateRetryLinkOptions = {}): RetryLink => {
  const { logger, maxAttempts = 3, initialDelayMs = 300, maxDelayMs = 3000 } = options;

  return new RetryLink({
    delay: {
      initial: initialDelayMs,
      max: maxDelayMs,
      jitter: true,
    },
    attempts: {
      max: maxAttempts,
      retryIf: (error, operation) => shouldRetryApolloError(error, operation, logger),
    },
  });
};
